import {
  Box,
  CloseIcon,
  Column,
  FilterIcon,
  FilterMenu,
  FilterMenuGroup,
  FilterMenuIconButton,
  FilterMenuList,
  FilterMenuOption,
  IconButton,
  MotionBox,
  Row,
  Spinner,
  Text,
} from "@hightouch/htui";

import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";

import { PanelHeader } from "src/components/panel-header";
import type { AssetCommentThreadsQuery } from "src/graphql";
import { useScrollFade } from "src/hooks/use-scroll-fade";

import { CommentThreadCard } from "./comment-thread-card";
import {
  filterThreads,
  filterThreadsToVersion,
  formatCommentAnchorTimestamp,
  isThreadResolved,
  type CommentFilters,
  groupInOrder,
} from "./utils";
import { useCommentLink } from "./use-comment-link";
import { useCommentThreads } from "./use-comment-threads";
import { useMentionSearch } from "./use-mention-search";

/** Filter menu values, which the checkbox group models as a string list. */
const SHOW_RESOLVED = "showResolved";
const ONLY_MINE = "onlyMine";
const ONLY_UNREAD = "onlyUnread";

type Props = {
  subject: CommentThreadSubject;
  /** Threads the page already loaded (a canvas loads every comment on it at once), in place of loading its own. */
  threads?: AssetCommentThreadsQuery["asset_comment_threads"];
  isLoading?: boolean;
  /** Scopes which review agents can be mentioned. Null when the surface has none. */
  brandProfileId?: string | null;
  selectedThreadId: string | null;
  /** The thread being pointed at elsewhere — for ads, its pin on the canvas. */
  hoveredThreadId?: string | null;
  onHoverThread?: (threadId: string | null) => void;
  /**
   * Owned by the page so any second view of the same threads hides them in
   * step — for ads, a thread filtered out of the rail shouldn't still be
   * pinned to the canvas.
   */
  filters: CommentFilters;
  /**
   * Shown when the subject has no comments yet. Says how to leave one, which
   * depends on the surface, so the caller supplies it.
   */
  emptyMessage: string;
  onFiltersChange: (filters: CommentFilters) => void;
  onSelectThread: (threadId: string | null) => void;
  onClose: () => void;
  /**
   * Whether the rail is actually showing. It stays mounted behind a slide-out
   * panel, and the thread query polls, so a closed rail would keep refetching.
   */
  isEnabled?: boolean;
  /** Hides threads from later versions — see `filterThreadsToVersion`. */
  versionCommentCutoff?: string | null;
  /** A short note shown above a thread, like a doc comment whose text was deleted. */
  threadNote?: (thread: { id: string; anchor?: unknown }) => string | null;
  threadSubtitle?: (
    thread: AssetCommentThreadsQuery["asset_comment_threads"][number],
  ) => string | null;
  /** Narrows the threads listed, like a doc's whole-doc comments without its inline ones. */
  threadFilter?: (thread: { anchor?: unknown }) => boolean;
  /** Groups the threads under a heading each, like a canvas's by file, keeping their order within a group. */
  threadGroup?: (
    thread: AssetCommentThreadsQuery["asset_comment_threads"][number],
  ) => string | null;
  /** Fades the list's clipped edges while it scrolls. */
  hasScrollFade?: boolean;
};

/**
 * The comments rail for one subject.
 *
 * A summary list only — selecting a thread is what opens it, wherever the
 * surface shows threads in full (for ads, inline beside its pin on the canvas).
 * Threads are read straight from Hasura and mutated through the custom
 * resolvers; the global mutation `onSuccess` invalidates the query, so nothing
 * here refetches by hand.
 *
 * Selecting doesn't mark the thread read: whatever renders it in full does
 * that, so a click here and a click on the canvas don't each fire a write.
 */
export const CommentsPanel = ({
  subject,
  threads: loadedThreads,
  isLoading: isLoadingThreads = false,
  brandProfileId,
  selectedThreadId,
  hoveredThreadId,
  onHoverThread,
  filters,
  emptyMessage,
  onFiltersChange,
  onSelectThread,
  onClose,
  isEnabled = true,
  versionCommentCutoff = null,
  threadNote,
  threadSubtitle,
  threadFilter,
  threadGroup,
  hasScrollFade = false,
}: Props) => {
  const scrollFade = useScrollFade();
  const searchMentions = useMentionSearch({ brandProfileId });
  const { highlightedCommentId, copyCommentLink } = useCommentLink();
  const {
    threads,
    isLoading: isQuerying,
    currentUserId,
    toComments,
    reply,
    toggleResolved,
    removeThread,
    isReplying,
    setThreadRead,
    isUnread,
    joinedAt,
  } = useCommentThreads({
    subject,
    threads: loadedThreads,
    isEnabled,
    // Every row can delete its thread, so only clear the selection when the
    // deleted one was it — otherwise deleting from the rail would close an
    // open card belonging to a different thread.
    onThreadDeleted: (threadId) => {
      if (threadId === selectedThreadId) {
        onSelectThread(null);
      }
    },
  });

  const isLoading = loadedThreads ? isLoadingThreads : isQuerying;

  // Ahead of the rail's filters, and not subject to the open thread's reprieve:
  // a thread from a later version doesn't exist as far as this render goes.
  // Openers from outside the rail return to the current version instead, where
  // every thread is visible.
  const threadsInVersion = threads
    ? filterThreadsToVersion(threads, versionCommentCutoff).filter(
        (thread) => threadFilter?.(thread) ?? true,
      )
    : [];
  const filteredThreads = filterThreads(
    threadsInVersion,
    filters,
    currentUserId,
    joinedAt,
    selectedThreadId,
  );
  const visibleThreads = threadGroup
    ? groupInOrder(filteredThreads, threadGroup)
    : filteredThreads;
  const selectedFilters = [
    ...(filters.showResolved ? [SHOW_RESOLVED] : []),
    ...(filters.onlyMine ? [ONLY_MINE] : []),
    ...(filters.onlyUnread ? [ONLY_UNREAD] : []),
  ];

  // Whatever is ticked in the menu, so the button agrees with it. Not "did
  // anything get hidden" — `showResolved` is off by default, so a subject with
  // one resolved thread would light the button up with nothing ticked.
  const hasActiveFilters = selectedFilters.length > 0;

  // Whether anything was actually hidden, which is a different question and the
  // one the empty state asks: with a filter on, an empty rail means the filters
  // excluded everything rather than that there are no comments.
  const isFiltered = threadsInVersion.length > visibleThreads.length;

  // What the cards' FLIP measures against. Measuring every render would catch
  // the one that opens the rail, and a closed rail parks off-canvas — so the
  // cards would glide in from the edge behind it.
  const threadOrder = visibleThreads.map((thread) => thread.id).join();

  const handleFilterChange = (selected: string[]) => {
    onFiltersChange({
      showResolved: selected.includes(SHOW_RESOLVED),
      onlyMine: selected.includes(ONLY_MINE),
      onlyUnread: selected.includes(ONLY_UNREAD),
    });
  };

  return (
    <Column height="100%" minHeight={0} bg="white">
      <PanelHeader
        title="Comments"
        actions={
          <>
            <FilterMenu>
              <FilterMenuIconButton
                aria-label="Filter comments"
                icon={FilterIcon}
                size="xs"
                variant={hasActiveFilters ? "primary" : "tertiary"}
              />
              <FilterMenuList>
                <FilterMenuGroup
                  type="checkbox"
                  value={selectedFilters}
                  onChange={handleFilterChange}
                >
                  <FilterMenuOption value={SHOW_RESOLVED}>
                    Show resolved comments
                  </FilterMenuOption>
                  <FilterMenuOption value={ONLY_MINE}>
                    Only your threads
                  </FilterMenuOption>
                  <FilterMenuOption value={ONLY_UNREAD}>
                    Only unread
                  </FilterMenuOption>
                </FilterMenuGroup>
              </FilterMenuList>
            </FilterMenu>

            <IconButton
              aria-label="Close comments"
              icon={CloseIcon}
              size="xs"
              onClick={onClose}
            />
          </>
        }
      />

      <Column
        ref={hasScrollFade ? scrollFade.ref : undefined}
        sx={hasScrollFade ? scrollFade.sx : undefined}
        flex={1}
        minHeight={0}
        overflowY="auto"
        p={1}
        gap={1}
      >
        {isLoading ? (
          <Row justify="center" p={6}>
            <Spinner size="lg" />
          </Row>
        ) : visibleThreads.length > 0 ? (
          visibleThreads.map((thread, index) => {
            const comments = toComments(thread);
            const isResolved = isThreadResolved(thread);
            const group = threadGroup?.(thread) ?? null;
            const previous = index > 0 ? visibleThreads[index - 1] : undefined;
            const startsGroup =
              threadGroup !== undefined &&
              (previous === undefined || threadGroup(previous) !== group);

            return (
              // `layout` gives the reorder a FLIP transition: a reply moves its
              // thread to the top of the list, and without this every card
              // below it jumps. Position only, so a card whose reply count just
              // changed doesn't distort while it travels. No `transition`:
              // Chakra shadows that prop on MotionBox, and FLIP has its own
              // spring — same as the nav items.
              <MotionBox
                key={thread.id}
                layout="position"
                layoutDependency={threadOrder}
              >
                {startsGroup && (
                  <Box px={3} pt={index === 0 ? 1 : 3} pb={1}>
                    <Text size="sm" fontWeight="medium" color="text.secondary">
                      {group ?? "Other"}
                    </Text>
                  </Box>
                )}
                {threadNote?.(thread) && (
                  <Box
                    borderLeftWidth="2px"
                    borderColor="base.border"
                    pl={2}
                    mx={3}
                    mt={2}
                  >
                    <Text size="sm" color="text.tertiary">
                      {threadNote(thread)}
                    </Text>
                  </Box>
                )}
                <CommentThreadCard
                  threadId={thread.id}
                  comments={comments}
                  isResolved={isResolved}
                  timestampLabel={formatCommentAnchorTimestamp(thread.anchor)}
                  subtitle={threadSubtitle?.(thread)}
                  isSelected={selectedThreadId === thread.id}
                  isHighlighted={hoveredThreadId === thread.id}
                  isUnread={isUnread(thread)}
                  onHover={(isHovered) =>
                    onHoverThread?.(isHovered ? thread.id : null)
                  }
                  searchMentions={searchMentions}
                  isReplying={isReplying}
                  onToggleRead={() =>
                    setThreadRead(thread.id, isUnread(thread))
                  }
                  onSelect={() => onSelectThread(thread.id)}
                  onReply={(draft) => reply(thread.id, draft)}
                  onToggleResolved={() => toggleResolved(thread.id, isResolved)}
                  // The rail shows only the opening comment, so deleting from
                  // here means deleting the whole thread.
                  onDeleteThread={() => removeThread(thread.id)}
                  highlightedCommentId={highlightedCommentId}
                  onCopyCommentLink={(commentId) =>
                    copyCommentLink({ commentId, isThreadResolved: isResolved })
                  }
                />
              </MotionBox>
            );
          })
        ) : (
          <Row justify="center" px={4} py={6}>
            <Text size="sm" color="text.secondary">
              {isFiltered ? "No comments match these filters." : emptyMessage}
            </Text>
          </Row>
        )}
      </Column>
    </Column>
  );
};
